import { Box, Stack } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";
import AssetHeroAdamCard from "../../../../Components/Assets/AssetHeroAdamCard/asset-hero-adam-card";
import { assetService, cacheService } from "../../../../Core/Api/services";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import { AdminAssetSingleDto } from "../../../../Core/Api/Types/db-asset-object-infos";
import { ObjectClass } from "../../../../Core/Api/Types/object-class";
import { ObjectLayer } from "../../../../Core/Api/Types/object-layer";
import { PlatformMode } from "../../../../Core/Api/Types/platform-mode";
import DashboardLayout from "../../../../Layouts/Dashboard/dashboard";
import {
  GetObject,
  GetObjectHierarchyDto,
  GetUpdateCounter,
  InitState,
  SetFetchedObject,
  SetLoading,
} from "../../../../Redux/Reducers/redux-slice-asset";
import { GetPlayerInfos, SetPlayerHeight, SetPlayerResponsive } from "../../../../Redux/Reducers/redux-slice-layout";
import { fetchInitialTree, TreeContext } from "../../../../Redux/Reducers/redux-slice-tree";
import { useAppDispatch, useAppSelector } from "../../../../Redux/redux-store";
import { routePaths } from "../../../../Router/router";
import { UserHasAcl } from "../../../../Utils/helpers";
import useAssetObjectUpdater from "../../../../Utils/Hooks/useAssetObjectUpdater";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import AssetSingleHeading from "./asset-single-heading";

function InventoryAssetSingle() {
  const { t } = useTranslation();
  const location = useLocation();
  const params = useParams();
  const assetId = (params.id && parseInt(params.id)) || 1;
  const [reload, setReload] = useState(0);

  const isAssetPreviewInventoryPage = location.pathname.indexOf("preview") >= 10;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const enqueueSnackbar = useCustomSnackbar();

  // Enable websocket live update
  useAssetObjectUpdater(assetId);

  const [assetData, setAssetData] = useState<AdminAssetSingleDto | undefined>();
  const asset = useSelector(GetObject);
  const assetHierarchy = useSelector(GetObjectHierarchyDto);

  const platformMode = cacheService.GetPlatformMode();
  const isDriveOnly = UserHasAcl(AclTypes.aclDistributionDriveOnly);

  const updateCounter = useSelector(GetUpdateCounter);

  // Resize player to leave space for HeroAdamCard
  const { containerHeight } = useAppSelector(GetPlayerInfos);
  const heroAdamCard = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (heroAdamCard.current) {
      dispatch(SetPlayerHeight(containerHeight - heroAdamCard.current.clientHeight));
      dispatch(SetPlayerResponsive(false));
    }

    const resizeObserver = new ResizeObserver((event) => {
      dispatch(SetPlayerHeight(containerHeight - event[0].borderBoxSize[0].blockSize));
      dispatch(SetPlayerResponsive(false));
    });

    if (heroAdamCard.current) resizeObserver.observe(heroAdamCard.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [heroAdamCard.current, containerHeight]);

  useEffect(() => {
    dispatch(SetLoading(true));
    dispatch(InitState());

    assetService.GetObjectLightById(assetId, 3).then((response) => {
      const asset = response.graph;
      if (asset) {
        if (asset.layer === ObjectLayer.Title) navigate(`${routePaths.title.path}/${asset.id}`);

        // dispatch asset in redux
        dispatch(SetFetchedObject(response));
        // get relevant data for page
        setAssetData(assetService.GetAdminAssetSingleDtoFromAssetObject(asset));
        // set hierarchy tree
        dispatch(
          fetchInitialTree({ context: TreeContext.TitlePanel, rootId: asset.parentTitleId!, currentId: asset.id })
        );
        dispatch(SetLoading(false));

        (platformMode === PlatformMode.Browser || asset.classification === ObjectClass.Drive) &&
          navigate(`${routePaths.asset.path}/${assetId}/${routePaths.assetPreview.path}`, {
            replace: true,
            state: location.state,
          });
      } else {
        // problem
        enqueueSnackbar(response.message || t("alert:error.unableToFetchAsset", { id: assetId }), "error");
        dispatch(SetLoading(false));
      }
    });
  }, [assetId, reload]);

  useEffect(() => {
    // This is updated by useAssetObjectUpdater (websocket)
    if (asset) setAssetData(assetService.GetAdminAssetSingleDtoFromAssetObject(asset));
  }, [updateCounter]);

  return (
    <DashboardLayout
      transparentTopbar
      stickiesVisibleAt={160}
      sxContent={{ display: "flex", flexDirection: "column", px: isAssetPreviewInventoryPage ? 0 : 2 }}
      Heading={<AssetSingleHeading assetId={assetId} assetData={assetData} setReload={setReload} />}
    >
      <Stack
        sx={{
          p: 0,
          mb: 0,
          height: "100%",
          ...(isAssetPreviewInventoryPage && {
            flex: 1,
            overflow: "hidden",
          }),
        }}
      >
        <Outlet />
      </Stack>
      {assetData && platformMode === PlatformMode.Adam && assetData.isTrailer && (
        <Box p={2} ref={heroAdamCard}>
          <AssetHeroAdamCard asset={assetData} setAssetData={setAssetData} />
        </Box>
      )}
    </DashboardLayout>
  );
}

export default InventoryAssetSingle;
